import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, TrendingUp, BadgeCheck, Loader2, Send, UserCheck, Lock } from "lucide-react";
import { FadeIn, SectionHeading } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_OPENINGS = [
  { title: "Office Coordinator", vertical: "Administration", location: "Alappuzha", type: "Full-time" },
  { title: "Business Development Manager", vertical: "Business Development", location: "Alappuzha", type: "Full-time" },
  { title: "Office Associate", vertical: "Administration", location: "Alappuzha", type: "Full-time" },
  { title: "Administrative Officer", vertical: "Administration", location: "Alappuzha", type: "Full-time" },
  { title: "HR Department", vertical: "HR Department", location: "Alappuzha", type: "Applications Closed", closed: true },
  { title: "Accounts Department", vertical: "Accounts Department", location: "Alappuzha", type: "Applications Closed", closed: true },
];

const FILTERS = ["All", "Administration", "Business Development", "HR Department", "Accounts Department"];

const PERKS = [
  { icon: GraduationCap, title: "Structured training programs", desc: "Onboarding, safety and skills training for every role, from shop floor to trade desk." },
  { icon: TrendingUp, title: "Clear career growth roadmaps", desc: "Four verticals mean four directions to grow — sideways, upward, or both." },
  { icon: BadgeCheck, title: "Certified, safe workplaces", desc: "ISO 9001:2015 systems and FSSAI-licensed operations you can verify on day one." },
];

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-brand-red/60";

export const Careers = () => {
  const [filter, setFilter] = useState("All");
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
  const [sending, setSending] = useState(false);
  const [openings, setOpenings] = useState(FALLBACK_OPENINGS);

  useEffect(() => {
    fetch(`${API}/openings`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((docs) => {
        if (Array.isArray(docs) && docs.length) setOpenings(docs);
      })
      .catch(() => {});
  }, []);

  const filters = ["All", ...new Set(openings.map((o) => o.vertical))];
  const roles = filter === "All" ? openings : openings.filter((o) => o.vertical === filter);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.position) {
      toast.error("Please fill in your name, email, phone and position.");
      return;
    }
    if (form.position === "HR Department" || form.position === "Accounts Department") {
      toast.error("Applications are closed for the HR and Accounts departments.");
      return;
    }
    const sel = openings.find((o) => o.title === form.position);
    if (sel?.closed) {
      toast.error(`Applications are closed for ${form.position}.`);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Application received. Our HR team will reach out to you.");
      setForm({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
    } catch {
      toast.error("Could not submit right now. Please try again or contact us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="careers" className="relative py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          kicker="03 — Careers"
          title="Build your career where you can verify the company."
          description="Review our open roles and apply online in two minutes — or walk into a branch and meet the team. Note: applications are closed only for the HR and Accounts departments."
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Openings + perks */}
          <div className="flex flex-col gap-10 lg:col-span-7">
            <FadeIn>
              <div className="flex flex-wrap gap-2" data-testid="careers-filter-group">
                {filters.map((f) => (
                  <button
                    key={f}
                    data-testid={`careers-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 sm:text-[11px] ${
                      filter === f
                        ? "border-brand-red bg-brand-red text-white"
                        : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </FadeIn>

            <FadeIn>
              <div className="flex items-start gap-4 rounded-2xl border border-brand-red/40 bg-brand-red/[0.08] p-6" data-testid="direct-recruitment-banner">
                <UserCheck className="mt-0.5 shrink-0 text-brand-red" size={24} />
                <div>
                  <h4 className="font-display text-lg font-semibold text-white sm:text-xl">
                    Direct recruitment — no agencies
                  </h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">
                    Recruitment at Techworld is handled directly by our institution's
                    director — a transparent and efficient hiring process, without
                    intermediaries or agencies. What you read here is exactly who you'll meet.
                  </p>
                </div>
              </div>
            </FadeIn>

            <div className="flex flex-col gap-3" data-testid="careers-openings-list">
              {roles.map((o, i) => (
                <FadeIn key={o.title} delay={i * 0.05}>
                  <button
                    data-testid="career-opening-card"
                    onClick={() =>
                      o.closed
                        ? toast.info(`Applications are closed for the ${o.vertical}.`)
                        : setForm((f) => ({ ...f, position: o.title }))
                    }
                    className="group flex w-full items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-5 text-left transition-all duration-300 hover:border-brand-red/40 hover:bg-white/[0.07] sm:p-6"
                  >
                    <div>
                      <h4 className="font-display text-lg font-semibold text-white sm:text-xl">{o.title}</h4>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 sm:text-[11px]">
                        {o.vertical} • {o.location} • {o.type}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                        o.closed
                          ? "border-white/15 text-slate-400"
                          : "border-brand-red/40 text-brand-bright group-hover:bg-brand-red group-hover:text-white"
                      }`}
                    >
                      {o.closed ? "Closed" : "Apply"}
                    </span>
                  </button>
                </FadeIn>
              ))}
              {roles.length === 0 && (
                <p className="rounded-xl border border-dashed border-white/15 p-6 text-sm text-slate-400">
                  No openings in this vertical right now — send a general application below.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {PERKS.map((p, i) => (
                <FadeIn key={p.title} delay={i * 0.08} className="h-full">
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors duration-300 hover:border-brand-red/40">
                    <p.icon className="text-brand-red" size={24} />
                    <h5 className="mt-4 font-display text-base font-semibold text-white">{p.title}</h5>
                    <p className="mt-2 text-xs leading-relaxed text-slate-400 sm:text-sm">{p.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>

          {/* Application form */}
          <FadeIn delay={0.1} className="lg:col-span-5">
            <div id="apply" className="scroll-mt-28 rounded-2xl border border-brand-red/30 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8 lg:sticky lg:top-24">
              <h3 className="font-display text-2xl font-semibold text-white">Apply now</h3>
              <p className="mt-2 text-sm text-slate-400">
                Fill this in — it reaches our HR desk directly.
              </p>
              <form data-testid="job-application-form" onSubmit={submit} className="mt-6 flex flex-col gap-4">
                <input data-testid="application-name-input" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="application-email-input" className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} />
                <input data-testid="application-phone-input" className={inputCls} type="tel" placeholder="Phone *" value={form.phone} onChange={set("phone")} />
                <select data-testid="application-position-select" className={`${inputCls} ${form.position ? "text-white" : "text-slate-500"}`} value={form.position} onChange={set("position")}>
                  <option value="" disabled className="bg-surface">Position applied for *</option>
                  {[...new Set([...openings.filter((o) => !o.closed).map((o) => o.title), "General Application"])].map((p) => (
                    <option key={p} value={p} className="bg-surface">{p}</option>
                  ))}
                </select>
                <input data-testid="application-experience-input" className={inputCls} placeholder="Experience (e.g. 3 years in export)" value={form.experience} onChange={set("experience")} />
                <textarea data-testid="application-message-input" className={`${inputCls} min-h-28 resize-y`} placeholder="Tell us briefly why you're a good fit" value={form.message} onChange={set("message")} />
                <button
                  data-testid="application-submit-button"
                  type="submit"
                  disabled={sending}
                  className="mt-2 flex items-center justify-center gap-2 rounded-full bg-brand-red px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-brand-bright hover:shadow-[0_0_28px_rgba(220,38,38,0.45)] disabled:opacity-60"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {sending ? "Submitting…" : "Submit Application"}
                </button>
              </form>
              <p data-testid="hr-accounts-closed-note" className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-slate-400">
                Note: applications are <span className="font-medium text-brand-bright">closed</span> for the HR and Accounts departments.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      <FadeIn>
        <div
          className="mt-16 flex flex-col items-center gap-6 rounded-3xl border border-brand-red/40 bg-brand-red/[0.08] px-6 py-12 text-center sm:py-16"
          data-testid="careers-cta-band"
        >
          <h3 className="max-w-2xl font-display text-2xl font-semibold text-white sm:text-4xl">
            Ready to join Techworld Business Group?
          </h3>
          <p className="max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Direct recruitment by our director — no agencies, no middlemen. Apply
            today and hear back from the desk that does the hiring.
          </p>
          <button
            data-testid="careers-apply-now-button"
            onClick={() => scrollToId("apply")}
            className="rounded-full bg-brand-red px-10 py-4 font-mono text-xs font-medium uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-brand-bright hover:shadow-[0_0_36px_rgba(220,38,38,0.5)]"
          >
            Apply Now
          </button>
        </div>
      </FadeIn>
    </section>
  );
};

export default Careers;
